import { CommonModule } from '@angular/common';
import { Component, ViewChild, AfterViewInit, NgZone, Output, EventEmitter, inject } from '@angular/core';
import { GoogleMapsLoaderService } from '../../services/google-maps-loader.service';
import { GoogleMap, GoogleMapsModule, MapInfoWindow, MapMarker, MapMarkerClusterer } from '@angular/google-maps';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { TranslatePipe } from '@ngx-translate/core';
import { RTLDivDirectiveDirective } from '../../directives/rtldiv-directive.directive';
import { ManagePanelModal } from './manage-panel-modal/manage-panel-modal';
import { MatCardModule } from '@angular/material/card';
import { combineLatest, Subject } from 'rxjs';
import { ApiQuery } from '../../web-services/api/api.query';
import { ResponsiveService } from '../../services/responsive.service';
import { API } from '../../types.api';
import _ from 'lodash';
import { TranslatorService } from '../../services/translator_service';
import { UtilService } from '../../services/util.service';
import { BinLiveInfoModal } from '../bin-live-info-modal/bin-live-info-modal';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';
import { GrahamScan } from './manage-panel-modal/graham-scan';
import { ManageBinsModal } from '../inventory/manage-bins-modal/manage-bins-modal';
import { AddClusterModal } from '../inventory/add-cluster-modal/add-cluster-modal';
import { takeUntil } from 'rxjs/operators';
import { ForceBinCollectionModal } from '../workplans-page/force-bin-collection-modal/force-bin-collection-modal';

type Point = [number, number];
const RADIUS = 54;
const mapInfoWindowOptions = { headerDisabled: true } as google.maps.InfoWindowOptions;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
@Component({
  host: {
		'(document:click)': 'handleClick($event)',
	},
  selector: 'app-map-live-view',
	imports: [TranslatePipe, MatDividerModule, MatCardModule, CommonModule, MatCheckboxModule,
		ManagePanelModal, RTLDivDirectiveDirective, GoogleMapsModule, GoogleMap],  
  templateUrl: './map-live-view.html',
  styleUrl: './map-live-view.scss',
})
export class MapLiveView {
  @ViewChild(GoogleMap, { static: false }) mapCmp!: GoogleMap;

	mapOptions: google.maps.MapOptions;
	isMobile: boolean = false;
	items: any[] = [];
	private clusterer: MarkerClusterer;
	markers: google.maps.Marker[] = [];
	private backUpMarkers: google.maps.Marker[] = [];
	@Output() showCMP = new EventEmitter<boolean>(true);
	spinnerActive: boolean = false;
	showClusters: boolean = false;
	isDisabled:boolean = false;
	sortedBins = [];
	binsHash = {};
	binsHashUsingSystem: {};
	usageType = 1;
	translationsObj;
	currentLang;
	capacityDisplayType: number;
	mapLiveView: google.maps.Map;
	private polygonStyle: google.maps.PolygonOptions = { draggable: false, editable: false };
	private isDrawingActive = false;
	private drawingVertices: google.maps.LatLng[] = [];
	private drawingPreview: google.maps.Polyline | null = null;
	private drawingVertexMarkers: google.maps.Marker[] = [];
	private drawingClickListener: google.maps.MapsEventListener | null = null;
	private drawButton: HTMLButtonElement | null = null;
	private drawControlDiv: HTMLElement | null = null;
	private settingsControlDiv: HTMLElement | null = null;
	infoWindows: google.maps.InfoWindow[] = [];
	infoWindow: any;
	backUpBins;
	SemiClustersPolygon: Array<any> = [];
	FullClustersPolygon: Array<any> = [];
	neighborhoodsPolygon: Array<any> = [];
	controlButton;
	polygon: any;
	binsIncluded:Array<any> = [];
	deletePolygonOverlay: any;
	deletePolygonButtonDiv: any;
	user_role_id = 0;
	preventManage: boolean = false;
	types = [];
	private readonly destroy$ = new Subject<void>();

	protected googleMapsLoader = inject(GoogleMapsLoaderService);

	constructor(private translator: TranslatorService, private apiQuery: ApiQuery, private zone: NgZone, 
		private responsiveService: ResponsiveService, private utilService: UtilService,private dialog: MatDialog) { 
		this.translator.currentLangEmitter$
		.subscribe(async value=>{
			this.translationsObj = await this.translator.getTranslation(value).toPromise();
			this.currentLang = value;
		})
		/* A language change re-loads the Maps API, so <google-map> is remounted as a
		   brand-new instance. Bins/clusters here are attached imperatively, so put
		   them back on the fresh map once it has rendered. */
		this.googleMapsLoader.reloaded$
		.pipe(takeUntil(this.destroy$))
		.subscribe(() => {
			setTimeout(() => this.applyDataToCurrentMap(), 0);
		});
	}

	/** Binds the bins currently held in `backUpBins` onto whichever map instance is mounted now. */
	private applyDataToCurrentMap() {
		if (this.backUpBins?.length && this.mapCmp?.googleMap) {
			this.mapLiveView = this.mapCmp.googleMap!;
			this.addYourLocationButton(this.mapLiveView);
			this.clearMarkersAndClusters();
			this.applyServerData(this.backUpBins, this.mapLiveView);
			this.spinnerActive = false;
		}
	}

	async ngOnInit() {
		await this.googleMapsLoader.loadGoogleMaps();
		this.mapOptions = {
			gestureHandling: 'greedy',
			streetViewControl: true,
			disableDefaultUI: false,
			mapTypeControl: false,
			fullscreenControl: true,
			fullscreenControlOptions: {
				position: google.maps.ControlPosition.TOP_LEFT
			},
			zoomControl: false
		};
		// If data arrived before the map was ready (race condition), apply it now.
		// setTimeout(0) lets Angular render the @if(googleMapsLoader.ready()) block first so mapCmp is available.
		setTimeout(() => this.applyDataToCurrentMap(), 0);
		this.onResize();
		this.responsiveService.checkWidth();
		this.spinnerActive = true;		

		this.types = [];
		this.items = [
			{ displayName: 'WIDGETS.RED_BINS', name: 'Red Bins', selected: true, color:"#EF8378"},
			{ displayName: 'WIDGETS.YELLOW_BINS', name: 'Yellow Bins', selected: true, color:"#E6E67E" },
			{ displayName: 'WIDGETS.GREEN_BINS', name: 'Green Bins', selected: true, color:"#66D699" },
			{ displayName: 'WIDGETS.NON_ACTIVE', name: 'Non Active', selected: true, color:"#9DA9AD" }
		];
		this.types = this.items.filter(item => item.selected).map(item => item.name);
	}

	onResize() {
		this.responsiveService.getMobileStatus().pipe(takeUntil(this.destroy$)).subscribe(isMobile => {
			this.isMobile = isMobile;
		});
	}

	ngOnDestroy(){
		this.destroy$.next();
		this.destroy$.complete();
		this.cancelDrawing();
		this.clearMarkersAndClusters();
		this.closeAllInfoWindows();
	}

	ngAfterViewInit(): void {
		setTimeout(() => {
			combineLatest([
				this.apiQuery.user$,
				this.apiQuery.filteredBasicBins$
			])
				.pipe(takeUntil(this.destroy$))
				.subscribe(([user, bins]) => {
					this.spinnerActive = true;
					if (!user) return;
					if (!bins || bins.length == 0) { return; }
					// Always save data so the map can render it once it becomes ready
					this.backUpBins = bins;
					if(user["usage_type_id"] != 1){
						this.usageType = user["usage_type_id"];
					}
					if(user["user_role_id"] == 3){
						this.preventManage = true;
					}
					this.user_role_id = user["user_role_id"];
					this.capacityDisplayType = user["user_default_capacity_type_id"];
					// Map may not be rendered yet if mapsReady is still false (async Maps load)
					if (!this.mapCmp?.googleMap) return;
					this.cancelDrawing();
					this.mapLiveView = this.mapCmp.googleMap!;
					this.spinnerActive = false;
					this.addYourLocationButton(this.mapLiveView);		
					this.clearMarkersAndClusters();
					this.applyServerData(bins, this.mapLiveView);
				})
		}, 0);
	}

	addYourLocationButton(map) {
		// A div pushed into map.controls stays registered even after its button is taken
		// out of the DOM, and Google attaches those divs asynchronously - so two calls in
		// the same tick both used to see getElementById() return null and both pushed.
		// Drop any control this component already registered, from the control array itself.
		const locationControls = map.controls[google.maps.ControlPosition.RIGHT_BOTTOM];
		for (let index = locationControls.getLength() - 1; index >= 0; index--) {
			const registered: any = locationControls.getAt(index);
			if (registered && registered.querySelector && registered.querySelector('#googleMapLiveView')) {
				locationControls.removeAt(index);
			}
		}
		let controlDiv = document.createElement('div');
		let firstChild = document.createElement('button');
		firstChild.type = 'button';
		firstChild.id = "googleMapLiveView";
		firstChild.style.backgroundColor = '#fff';
		firstChild.style.border = 'none';
		firstChild.style.outline = 'none';
		firstChild.style.width = '40px';
		firstChild.style.height = '40px';
		firstChild.style.borderRadius = '2px';
		firstChild.style.boxShadow = '0 1px 4px rgba(0,0,0,0.3)';
		firstChild.style.cursor = 'pointer';
		firstChild.style.marginRight = '10px';
		firstChild.style.padding = '0px';
		firstChild.title = 'Your Location';
		controlDiv.appendChild(firstChild);
		
		let secondChild = document.createElement('div');
		secondChild.style.margin = 'calc(50% - 18px / 2)';
		secondChild.style.width = '18px';
		secondChild.style.height = '18px';
		secondChild.style.backgroundImage = 'url(https://maps.gstatic.com/tactile/mylocation/mylocation-sprite-1x.png)';
		secondChild.style.backgroundSize = '180px 18px';
		secondChild.style.backgroundPosition = '0px 0px';
		secondChild.style.backgroundRepeat = 'no-repeat';
		secondChild.id = 'center-mapview';
		firstChild.appendChild(secondChild);
		
		google.maps.event.addListener(map, 'dragend', function() {
			document.getElementById('center-mapview').style.backgroundPosition = '0px 0px';
		});

		firstChild.addEventListener('click', function() {
			var imgX = '0';
			var animationInterval = setInterval(function(){
				if(imgX == '-18') imgX = '0';
				else imgX = '-18';
				document.getElementById('center-mapview').style.backgroundPosition = imgX+'px 0px';
			}, 500);
			if (navigator.geolocation) {
				navigator.geolocation.getCurrentPosition( pos => {        
				map.setCenter({lat: pos.coords.latitude, lng: pos.coords.longitude});
				map.setZoom(15);
				clearInterval(animationInterval);
				document.getElementById('center-mapview').style.backgroundPosition = '-144px 0px';
				}, error => {
				},{maximumAge:10000, timeout:5000, enableHighAccuracy: true});
			}else{
				clearInterval(animationInterval);
				document.getElementById('center-mapview').style.backgroundPosition = '0px 0px';
			}
		});
		locationControls.push(controlDiv);
	}

	private applyServerData(bins: any[], map: any) {
		this.sortedBins = this.sortBinsAccordingToCoordinates(bins);	
		this.binsHash = this.mapBinsByCoordinates(this.sortedBins);		
		this.binsHashUsingSystem = this.mapBinsByClusterUsingSystem(this.sortedBins);  
		// Build markers OUTSIDE Angular to avoid change detection work
		this.zone.runOutsideAngular(() => {
			const markers: google.maps.Marker[] = [];
			const bounds = new google.maps.LatLngBounds();
			// build markers FROM binsHash (not from bins)
			for (const [coordKey, binArray] of Object.entries(this.binsHash)) {
				let isPartOfCluster = false;
				const checkIfCluster = 	this.mapBinsByClusterUsingSystem(binArray);	
				_.forOwn(checkIfCluster, (value :any, key) => {
					if(value.length > 1){
						isPartOfCluster = true;
					}					
				})
				
				const b = binArray[0];
				const icon = binArray?.['length'] > 1 ? this.clusterToIcon(binArray) : this.BinToIcon(b.Bin_live_Data.color_status_id, b.bin_collection_mode);
				const label = binArray?.['length'] > 1 ? this.labelOption(binArray,icon) : '';
				
				const pos: google.maps.LatLngLiteral = {
					lat: b.Bin_location.bin_latitude,
					lng: b.Bin_location.bin_longitude,
				};
		
				const infoWindow = new google.maps.InfoWindow(mapInfoWindowOptions);
				this.wireMapInfoWindowCloseButton(infoWindow);

				const marker = new google.maps.Marker({
					position: pos,
					icon,
					label:label
				});

				marker['lat'] = Number(b.Bin_location.bin_latitude);
				marker['lng'] = Number(b.Bin_location.bin_longitude);					
				marker['category'] = binArray?.['length'] > 1 ? this.convertMarkerToColor(icon) : this.BinToType(b.Bin_live_Data.color_status_id,b["bin_collection_mode"]);
				marker['visible'] = true;
				marker['cluster'] = binArray?.['length'] > 1 ? true : false;
				marker['draggable'] = false;
				marker['site_id'] = b.site_id;
				marker['partOfCluster'] = binArray?.['length'] > 1 ? isPartOfCluster : this.isPartOfCluster(this.binsHashUsingSystem,b);
				marker['nearbyBin'] = b.nearbyBin;

				if(binArray?.['length'] > 1){
					marker['item'] = binArray;
				}else{
					marker['bin_name'] = b.bin_name;
					marker['bin_id'] = b.bin_id;
					marker['fill_level_percent'] = b.Bin_live_Data.fill_level_percent;
					marker['bin_address'] = b.Bin_location.bin_address;
					marker['bin_neighbourhood'] = b.Bin_location.bin_neighbourhood;
					marker['last_collection_time'] = b.Bin_live_Data.last_collection_time;
					marker['color_status_name'] = b.Bin_live_Data.color_status_name;
					marker['bin_max_capacity_in_liters'] = b.Bin_thresholds.bin_max_capacity_in_liters;
					marker['average_daily_fill_rate_in_percent'] = b.average_daily_fill_rate_in_percent;
				}

				marker.addListener("click", () => {
					this.setWorkplanBinsFromMarker(marker);
					const content = this.binToHtml(marker);
					infoWindow.setContent(content);
					this.closeAllInfoWindows();
					infoWindow.open(map, marker);
				});		

				this.infoWindows.push(infoWindow);
		
				markers.push(marker);
				bounds.extend(pos);

				
				google.maps.event.addListener(infoWindow, "domready", () => {
					const btn = document.getElementById("bin-action-btn");
					if (btn) {
						btn.addEventListener("click", () => {
						this.onBinInfoClick(marker);
						});
					}
				});
			}
	
			this.markers = markers;
			this.backUpMarkers = markers;
			const size52 = new google.maps.Size(52, 52);
			// Bucket counts so you reuse a tiny set of icons instead of unique text per cluster
			const labelFor = (count:number) => {
				return { text: String(count), color: "#2B3733", fontSizePx: 16 };			
			};

			const iconCache = new Map<string, google.maps.Icon>();

			function svgIcon(label: string): google.maps.Icon {
				let icon = iconCache.get(label);
				if (icon) return icon;
				const svg = `
					<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="53" height="53" viewBox="0 0 53 53">
						<image xlink:href="data:image/png;base64,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" x="0" y="0" width="53" height="53"/>
							  <text x="26" y="30" font-family="Roboto, Arial, sans-serif" font-size="11" font-weight="bold"
								text-anchor="middle" fill="#3A4743">${label}</text>
					</svg>`;
				icon = {
				  url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
				  scaledSize: size52,
				};
				iconCache.set(label, icon);
				return icon;
			}

			const customRenderer = {
				render: ({ count, position }: {count: number; position: google.maps.LatLng}) => {
				  const label = labelFor(count);
				  return new google.maps.Marker({
					position,
					icon: svgIcon(label.text),
					optimized: true, // ensure canvas-optimized rendering
					clickable: true,
				  });
				},
			};

			// fit & cap zoom
			if (!bounds.isEmpty()) {
				map.fitBounds(bounds);
				google.maps.event.addListenerOnce(map, "bounds_changed", () => {
					if (map.getZoom()! > 15) map.setZoom(15);
				});
			}

			//handle click on map 
			map.addListener("click", () => {
				if (this.isDrawingActive) { return; }
				this.closeAllInfoWindows();
			});
			
			let not_same_linked_sites_id = true;
			if(JSON.parse(sessionStorage.getItem("chosenSites"))){
				let sitesToCompare = [];
				let isChildsSites = false;
				_.each(JSON.parse(sessionStorage.getItem("chosenSites")), site => {
					if(site != 0){
						sitesToCompare.push(site);
					}
				});	
				isChildsSites = sitesToCompare.some(site => site.linked_sites_id != 0);
				if(sitesToCompare.length == 1){
					not_same_linked_sites_id = false;
				}else{
					if(isChildsSites){
						not_same_linked_sites_id = sitesToCompare.some(site => site.linked_sites_id != sitesToCompare[0].linked_sites_id);
					}
				}
			}

			if(!not_same_linked_sites_id){
				//this.markers.forEach(marker => marker.setMap(map));
				this.clusterer = new MarkerClusterer({
					map,
					markers: this.markers,
					algorithm: new SuperClusterAlgorithm({
					  minPoints: this.markers.length + 1,
					  radius: 1,
					  maxZoom: 22,
					})
				  });
				// Same asynchronous attach as the polygon control: document.getElementById
				// could not see a div pushed a moment earlier, so two calls in one tick both
				// created a settings control. The control array answers synchronously.
				let settingsControlDiv = this.findRegisteredControl(map, google.maps.ControlPosition.TOP_RIGHT, 'settings-id');
				if(!settingsControlDiv && !this.isMobile){
					const centerControlDiv = document.createElement('div');
					centerControlDiv.id = 'settings-id';
					centerControlDiv.style.margin = '14px 24px';
					centerControlDiv.style.width = '32px';
					centerControlDiv.style.height = '32px';
					centerControlDiv.style.background = 'white';
					centerControlDiv.style.textAlign = 'center';
					const centerControl = this.createCenterControl();
					centerControlDiv.appendChild(centerControl);
					map.controls[google.maps.ControlPosition.TOP_RIGHT].push(centerControlDiv);
					settingsControlDiv = centerControlDiv;
				}
				this.settingsControlDiv = settingsControlDiv;
				if(this.settingsControlDiv){
					this.settingsControlDiv.style.display = 'block';			
				}

				if (!this.isMobile) {
					this.ensureDrawPolygonControl(map);
				}
			}else{
				// SuperCluster-based algorithm is great for large datasets
				this.clusterer = new MarkerClusterer({
					map,
					markers: this.markers,
					algorithm: new SuperClusterAlgorithm({
						radius: 90,   // cluster radius in px
						maxZoom: 18,  // stop clustering at this zoom
						minPoints: 3 // min markers per cluster
					}),
					renderer: customRenderer
				});
				if(this.settingsControlDiv){
					this.settingsControlDiv.style.display = 'none';			
				}
				this.cancelDrawing();
				this.hideDrawPolygonControl();
			}
		});
	}

	// --- Custom polygon drawing (replaces the removed google.maps.drawing.DrawingManager) ---

	ensureDrawPolygonControl(map: google.maps.Map) {
		// Google attaches a div pushed into map.controls asynchronously, so looking the
		// control up through document.getElementById let two calls in the same tick both
		// find nothing and both push - that is the duplicated polygon icon. The control
		// array is updated synchronously and belongs to this map instance, so a control
		// left over from a previous map is never mistaken for the current one.
		const existing = this.findRegisteredControl(map, google.maps.ControlPosition.TOP_CENTER, 'draw-polygon-control');
		if (existing) {
			existing.style.display = 'block';
			this.drawControlDiv = existing;
			return;
		}
		const controlDiv = document.createElement('div');
		controlDiv.id = 'draw-polygon-control';
		controlDiv.style.margin = '10px';
		controlDiv.style.backgroundColor = '#fff';
		controlDiv.style.borderRadius = '2px';
		controlDiv.style.boxShadow = '0 1px 4px rgba(0,0,0,0.3)';

		const button = document.createElement('button');
		button.type = 'button';
		button.style.display = 'flex';
		button.style.alignItems = 'center';
		button.style.justifyContent = 'center';
		button.style.border = 'none';
		button.style.outline = 'none';
		button.style.background = 'transparent';
		button.style.height = '36px';
		button.style.padding = '0 10px';
		button.style.cursor = 'pointer';
		button.addEventListener('click', () => this.zone.run(() => this.toggleDrawing(map)));

		controlDiv.appendChild(button);
		map.controls[google.maps.ControlPosition.TOP_CENTER].push(controlDiv);
		this.drawControlDiv = controlDiv;
		this.drawButton = button;
		this.setDrawButtonActive(false);
	}

	hideDrawPolygonControl() {
		const controlDiv = this.drawControlDiv;
		if (controlDiv) { controlDiv.style.display = 'none'; }
	}

	/** Controls already registered on this map, read synchronously from map.controls. */
	private findRegisteredControl(map: google.maps.Map, position: any, id: string): HTMLElement | null {
		const controls = map.controls[position];
		for (let index = 0; index < controls.getLength(); index++) {
			const registered: any = controls.getAt(index);
			if (registered && registered.id === id) {
				return registered as HTMLElement;
			}
		}
		return null;
	}

	toggleDrawing(map: google.maps.Map) {
		if (this.isDrawingActive) {
			// Closing the shape is done by clicking the first point; the button now cancels an in-progress drawing.
			this.cancelDrawing();
		} else {
			if (this.polygon && this.polygon.getMap()) {
				this.updateDrawButtonTitle();
				return;
			}
			this.startDrawing(map);
		}
	}

	startDrawing(map: google.maps.Map) {
		this.cancelDrawing();
		this.isDrawingActive = true;
		this.setDrawButtonActive(true);
		map.setOptions({ draggableCursor: 'crosshair', disableDoubleClickZoom: true });

		this.drawingClickListener = map.addListener('click', (e: google.maps.MapMouseEvent) => {
			if (!e.latLng) { return; }
			this.zone.run(() => this.addDrawingVertex(e.latLng));
		});
	}

	private addDrawingVertex(latLng: google.maps.LatLng) {
		this.drawingVertices.push(latLng);
		this.updateDrawingPreview();
	}

	private updateDrawingPreview() {
		if (!this.drawingPreview) {
			// Use an open polyline while drawing so the shape isn't auto-closed/filled into triangles.
			this.drawingPreview = new google.maps.Polyline({
				strokeColor: '#1A73E8',
				strokeOpacity: 1,
				strokeWeight: 2,
				clickable: false,
				map: this.mapLiveView,
			});
		}
		this.drawingPreview.setPath(this.drawingVertices);

		this.clearDrawingVertexMarkers();
		this.drawingVertices.forEach((pos, index) => {
			// The first vertex is the "close" handle: clicking it finishes the polygon.
			const isFirst = index === 0;
			const canClose = isFirst && this.drawingVertices.length >= 3;
			const marker = new google.maps.Marker({
				position: pos,
				map: this.mapLiveView,
				clickable: isFirst,
				cursor: canClose ? 'pointer' : undefined,
				zIndex: isFirst ? 2 : 1,
				icon: {
					path: google.maps.SymbolPath.CIRCLE,
					scale: isFirst ? 6 : 5,
					fillColor: isFirst ? '#28B365' : '#FFFFFF',
					fillOpacity: 1,
					strokeColor: isFirst ? '#FFFFFF' : '#1A73E8',
					strokeWeight: 2,
				},
			});
			if (isFirst) {
				marker.addListener('click', () => {
					if (this.drawingVertices.length >= 3) {
						this.zone.run(() => this.finishDrawing());
					}
				});
			}
			this.drawingVertexMarkers.push(marker);
		});
	}

	finishDrawing() {
		if (this.drawingVertices.length < 3) {
			this.cancelDrawing();
			return;
		}
		const path = this.drawingVertices.slice();
		this.teardownDrawingTool();

		if (this.polygon) {
			this.polygon.setMap(null);
		}
		this.polygon = new google.maps.Polygon({
			...this.polygonStyle,
			paths: path,
			map: this.mapLiveView,
		});
		this.addPolygonChangeEvent(this.polygon);
		this.openPolygonInfoWindow();
		this.addDeletePolygonButton(this.polygon);
		this.updateDrawButtonTitle();
	}

	cancelDrawing() {
		this.teardownDrawingTool();
	}

	private teardownDrawingTool() {
		this.isDrawingActive = false;
		this.setDrawButtonActive(false);
		if (this.drawingClickListener) {
			this.drawingClickListener.remove();
			this.drawingClickListener = null;
		}
		if (this.drawingPreview) {
			this.drawingPreview.setMap(null);
			this.drawingPreview = null;
		}
		this.clearDrawingVertexMarkers();
		this.drawingVertices = [];
		if (this.mapLiveView) {
			this.mapLiveView.setOptions({ draggableCursor: null, disableDoubleClickZoom: false });
		}
	}

	private clearDrawingVertexMarkers() {
		this.drawingVertexMarkers.forEach(marker => marker.setMap(null));
		this.drawingVertexMarkers = [];
	}

	private setDrawButtonActive(active: boolean) {
		if (!this.drawButton) { return; }
		this.drawButton.innerHTML = this.drawButtonIcon(active);
		this.drawButton.style.backgroundColor = active ? '#E8F5EE' : 'transparent';
		this.updateDrawButtonTitle();
	}

	private updateDrawButtonTitle() {
		if (!this.drawButton) { return; }
		const isHebrew = this.currentLang == 'iw';
		if (this.polygon && this.polygon.getMap()) {
			this.drawButton.title = isHebrew
				? 'יש למחוק את האזור הקיים לפני יצירת אזור חדש'
				: 'Please remove the existing area before creating a new one';
		} else if (this.isDrawingActive) {
			this.drawButton.title = isHebrew
				? 'לחץ על המפה להוספת נקודות, ולחץ על הנקודה הראשונה לסגירת האזור'
				: 'Click on the map to add points, then click the first point to close the area';
		} else {
			this.drawButton.title = isHebrew
				? 'צייר אזור לבחירת פחים'
				: 'Draw a shape to select bins';
		}
	}

	private drawButtonIcon(active: boolean): string {
		const color = active ? '#28B365' : '#5D7870';
		return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
			<polygon points="4,7 12,3 20,8 17,19 7,18" fill="none" stroke="${color}" stroke-width="2" stroke-linejoin="round"/>
			<circle cx="4" cy="7" r="2" fill="${color}"/>
			<circle cx="12" cy="3" r="2" fill="${color}"/>
			<circle cx="20" cy="8" r="2" fill="${color}"/>
			<circle cx="17" cy="19" r="2" fill="${color}"/>
			<circle cx="7" cy="18" r="2" fill="${color}"/>
		</svg>`;
	}

	addDeletePolygonButton(polygon) {
		// Remove previous button if exists
		if (this.deletePolygonOverlay) {
			this.deletePolygonOverlay.setMap(null);
			this.deletePolygonOverlay = null;
		}
		if (this.deletePolygonButtonDiv && this.deletePolygonButtonDiv.parentNode) {
			this.deletePolygonButtonDiv.parentNode.removeChild(this.deletePolygonButtonDiv);
			this.deletePolygonButtonDiv = null;
		}

		// Place icon at the bottom-most vertex of the polygon (lowest latitude)
		const path = polygon.getPath().getArray();
		let lat = path[0].lat();
		let lng = path[0].lng();
		for (let i = 1; i < path.length; i++) {
		  if (path[i].lat() < lat) {
			lat = path[i].lat();
			lng = path[i].lng();
		  }
		}

		// Create button div
		const buttonDiv = document.createElement('div');
		buttonDiv.style.position = 'absolute';
		buttonDiv.style.zIndex = '1000';
		buttonDiv.style.transform = 'translate(-50%, -50%)';
		buttonDiv.innerHTML = `<img src="assets/images/dashboard/delete-polygon.svg" style="width:32px;height:32px;cursor:pointer"/>`;

		// Add to map as overlay
		const overlay = new google.maps.OverlayView();
		overlay.onAdd = function() {
			const panes = this.getPanes();
			panes.overlayMouseTarget.appendChild(buttonDiv);
		};
		overlay.draw = function() {
			const projection = this.getProjection();
			const position = projection.fromLatLngToDivPixel(new google.maps.LatLng(lat, lng));
			buttonDiv.style.left = position.x + 'px';
			buttonDiv.style.top = position.y + 'px';
		};
		overlay.onRemove = function() {
			if (buttonDiv.parentNode) {
				buttonDiv.parentNode.removeChild(buttonDiv);
			}
		};
		overlay.setMap(this.mapLiveView);

		// Store reference for later removal
		this.deletePolygonButtonDiv = buttonDiv;
		this.deletePolygonOverlay = overlay;

		// Click event
		buttonDiv.onclick = () => {
			polygon.setMap(null);
			overlay.setMap(null);
			if (this.infoWindow) this.infoWindow.close();
			this.polygon = null;
			this.updateDrawButtonTitle();
		};

		// Update button position if polygon is edited
		google.maps.event.addListener(polygon.getPath(), 'set_at', () => overlay.draw());
		google.maps.event.addListener(polygon.getPath(), 'insert_at', () => overlay.draw());
		google.maps.event.addListener(polygon, 'dragend', () => overlay.draw());
	}

	addPolygonChangeEvent(polygon) {
		var me = polygon,
		  isBeingDragged = false,
		  triggerCoordinatesChanged = function () {
			// Broadcast normalized event
			google.maps.event.trigger(me, 'coordinates_changed');
		  };
		  google.maps.event.addListener(me, 'click', (event) => {	
			this.openPolygonInfoWindow(event);
		});

		google.maps.event.addListener(me, 'dragstart', function () {			
		  isBeingDragged = true;
		});
	
		// If the overlay is dragged
		google.maps.event.addListener(me, 'dragend', function () {
		  triggerCoordinatesChanged();
		  isBeingDragged = false;
		});
	
		// Or vertices are added to any of the possible paths, or deleted
		var paths = me.getPaths();
		paths.forEach(function (path, i) {
		  google.maps.event.addListener(path, 'insert_at', function () {
			triggerCoordinatesChanged();
		  });
		  google.maps.event.addListener(path, 'set_at', function () {
			if (!isBeingDragged) {
			  triggerCoordinatesChanged();
			}
		  });
		  google.maps.event.addListener(path, 'remove_at', function () {
			triggerCoordinatesChanged();
		  });
		});
	}

	openPolygonInfoWindow(event?:any){
		this.closeAllInfoWindows();
		if(this.infoWindow){
			this.infoWindow.close();
		}
		this.infoWindow = new google.maps.InfoWindow(mapInfoWindowOptions);
		this.wireMapInfoWindowCloseButton(this.infoWindow);
		let collectedBins = [];
		let avgFillLevel = 0;
		let collectionCoverage = 0;

		this.binsIncluded = [];
		let paths = this.getPaths();
		_.each(this.markers, marker => {
			if(marker['visible']){
				if(marker['item']){
					_.each(marker['item'], item => {
						if(this.isWithinCoordinates([item.Bin_location.bin_latitude,item.Bin_location.bin_longitude],paths)){
							this.binsIncluded.push(item);
						}
					})
				}else{
					if(this.isWithinCoordinates([marker['lat'],marker['lng']],paths)){
						this.binsIncluded.push(marker);
					}
				}
			}
		});
		_.each(this.binsIncluded, (bin) => {
			if(moment(moment(bin.last_collection_time).format('YYYY-MM-DD')).isBetween(moment().startOf('month').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD'))){
				collectedBins.push(bin);
			}
			if(bin.average_daily_fill_rate_in_percent){
				avgFillLevel += bin.average_daily_fill_rate_in_percent;
			}
		});

		avgFillLevel = Number(Number(avgFillLevel / this.binsIncluded.length).toFixed(0));
		collectionCoverage = Number(Number((collectedBins.length / this.binsIncluded.length) * 100).toFixed(0));
		const progress = avgFillLevel / 100;
		const dashoffset = CIRCUMFERENCE * (1 - progress);
		this.polygon["avgFillLevel"] = isNaN(avgFillLevel) ? 0 : avgFillLevel;
		this.polygon["collectionCoverage"] = isNaN(collectionCoverage) ? 0 : collectionCoverage;

		let contentString = `
		<div>
			<div style="padding-top: 10px;">
				<div style="display: flex;justify-content: space-between;align-items: center;">
					<div style="display: flex;gap: 12px;align-items: center;">
						<img src="assets/images/dashboard/greenBin.svg" alt=""/>
						<div style="display: flex;gap: 4px;align-items: center;">
							<span style="font-family: Open Sans, sans-serif;font-style: normal;font-weight: 600; font-size: 14px;color:#2A3338;align-items: center;display: flex">${this.polygon["collectionCoverage"]}%</span>
							<span style="font-family: Open Sans, sans-serif;font-style: normal;font-weight: 400; font-size: 12px;color:#2A3338;">${this.translationsObj.TITLES.COLLECTED_BINS}</span>
						</div>
					</div>
					<div style="display: flex;align-items: center;gap: 12px;">
						${(this.user_role_id == 1 || this.user_role_id == 2 || this.user_role_id == 4 || this.user_role_id == 5 || this.user_role_id == 6) && !this.isMobile ? 
							`<div class="${this.currentLang == 'iw' ? 'dropstart' : ''}">
							<div class="dropdown-toggle map-infowindow-dots" data-bs-toggle="dropdown" style="cursor: pointer;display: flex;">
								<img alt="three-dots" src="assets/images/dashboard/threedots.svg">
							</div>						
							<div class="dropdown-menu map-infowindow-dropdown ${this.currentLang == 'iw' ? 'rtl' : ''}">     
								<div style="color: var(--Dark-Green-800, #485760); font-family: Open Sans, sans-serif; font-size: 12px; font-style: normal; font-weight: 400; display: flex; padding: 4px 0px;">
									<span id="click-future-workplan" style="cursor: pointer;">${this.translationsObj.WORKPLAN.ADD_BIN_TO_FUTURE_WORKPLAN}</span>
								</div>
							</div>
						</div>`	: ``}
						<div class="map-infowindow-close-row" style="display:flex;">
							<button type="button" class="map-infowindow-close" style="border: none;background: transparent;padding: unset;display: flex;">
								<img src="assets/images/dashboard/close_infoWindow.svg" alt=""/>
							</button>
						</div>
					</div>	
				</div>	
				<div style="display: flex;gap: 12px;padding-top: 20px;align-items: center;">
					<div class="circle">
						<svg style="transform: rotate(-90deg)" width="24" height="24" viewBox="0 0 120 120">
							<circle style="fill: none;stroke: #CDD2D4" class="progress__meter" cx="60" cy="60" r="55" stroke-width="10" />
							<circle style="stroke-dashoffset:${dashoffset};stroke-dasharray:${CIRCUMFERENCE};fill: none;stroke: #28B365;stroke-linecap: round" class="progress__value averageCapacity" cx="60" cy="60" r="55" stroke-width="10" />
						</svg>
					</div>
					<div style="display: flex;gap: 4px;align-items: center;">
						<span style="font-family: Open Sans, sans-serif;font-style: normal;font-weight: 600; font-size: 14px;color:#2A3338;">${this.polygon["avgFillLevel"]}%</span>
						<span style="font-family: Open Sans, sans-serif;font-style: normal;font-weight: 400; font-size: 12px;color:#2A3338;">${this.translationsObj.LIVE_INFO.AVG_RATE}</span>
					</div>
				</div>
				<div style="display: flex;gap: 16px;align-items: center;justify-content: center;padding-top: 15px;">        
					${this.user_role_id != 3 ? `<button id="create-cluster" style="display: flex;justify-content: center;align-items: center;padding: 8px 12px;background: rgb(40, 179, 101);border-radius: 30px;border: none;font-family: Open Sans, sans-serif;font-style: normal;font-weight: 400;font-size: 12px;color: white;outline: none;gap: 4px">
					<span>${this.translationsObj.MANAGE_BIN.CREATE_CLUSTER}</span>
					<img src="assets/images/dashboard/edit-whitePen.png" alt="">
					</button>	
					<button id="manage-bins" style="display: flex;justify-content: center;align-items: center;padding: 8px 12px;background: white;border-radius: 30px;border: 1px solid #5D7870;font-family: Open Sans, sans-serif;font-style: normal;font-weight: 400;font-size: 12px;color: #5D7870;outline: none;gap: 4px">
						<span>${this.translationsObj.MANAGE_BIN.MANAGE_BINS}</span>
						<img src="assets/images/dashboard/edit-grayPen.png" alt="">
					</button>` : ``}
				</div>
			</div>
		</div>`;
		this.infoWindow.setContent(contentString);
		if(this.binsIncluded.length > 0 && !event){
			let maxLat = this.binsIncluded[0].lat;
			let minLat = this.binsIncluded[0].lat;
			let maxLng = this.binsIncluded[0].lng;
			let minLng = this.binsIncluded[0].lng;
	
			_.each(this.binsIncluded, (bin) => {
				if(bin.lat > maxLat){
					maxLat = bin.lat;
				}	
				if(bin.lat < minLat){
					minLat = bin.lat;
				}	
				if(bin.lng > maxLng){
					maxLng = bin.lng;
				}	
				if(bin.lng < minLng){
					minLng = bin.lng;
				}			
			});
	
			let centerLat =  minLat + ((maxLat - minLat) / 2);
			let centerLng = minLng + ((maxLng - minLng) / 2);
			this.infoWindow.setPosition({lat: centerLat, lng: centerLng});
		}else if(event){
			this.infoWindow.setPosition(event.latLng);
		}
		this.infoWindow.open(this.mapLiveView);
	}

	private isWithinCoordinates = (point: Point, polygon: Array<Point>) => {		
		const [lat, lng] = point;		
		var isInside = false;
		for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
		  const iLat = polygon[i][0];
		  const iLng = polygon[i][1];
	  
		  const jLat = polygon[j][0];
		  const jLng = polygon[j][1];
	  
		  const isIntersecting =
			iLng > lng != jLng > lng &&
			lat < ((jLat - iLat) * (lng - iLng)) / (jLng - iLng) + iLat;
	  
		  if (isIntersecting) isInside = !isInside;
		}
	  
		return isInside;
	}

	private setWorkplanBinsFromMarker(marker: google.maps.Marker & Record<string, any>): void {
		if (marker['item']?.length) {
			this.binsIncluded = [...marker['item']];
		} else if (marker['bin_id'] != null && this.backUpBins?.length) {
			const full = this.backUpBins.find((b: any) => b.bin_id === marker['bin_id']);
			this.binsIncluded = full ? [full] : [];
		} else {
			this.binsIncluded = [];
		}
	}

	/** Full bin records aligned with `backUpBins` for the force workplan modal. */
	private getBinsForForceCollectionModal(): any[] {
		if (!this.binsIncluded?.length) {
			return [];
		}
		if (!this.backUpBins?.length) {
			return this.binsIncluded.slice();
		}
		const out: any[] = [];
		const seen = new Set<any>();
		for (const entry of this.binsIncluded) {
			const id = entry?.bin_id;
			if (id == null || seen.has(id)) {
				continue;
			}
			seen.add(id);
			const full = this.backUpBins.find((b: any) => b.bin_id === id);
			out.push(full || entry);
		}
		return out;
	}

	handleClick(event) { 
		let manageBins = document.getElementById('manage-bins');
		let createCluster = document.getElementById('create-cluster');
		let clickFutureWorkplan = document.getElementById('click-future-workplan');
		if(clickFutureWorkplan && clickFutureWorkplan.contains(event.target)){
			const preselectedBins = this.getBinsForForceCollectionModal();
			const data = preselectedBins.length ? { preselectedBins } : undefined;
			if(this.isMobile){
				this.dialog.open(ForceBinCollectionModal, { panelClass: 'popup-container-mobile', data });
			}else{
				this.dialog.open(ForceBinCollectionModal, { data });
			}
		}
		if(manageBins && manageBins.contains(event.target) && !this.preventManage){
			this.manageBins();
		}else if(createCluster && createCluster.contains(event.target)){
			let class_name = '';
			if(this.isMobile){
				class_name = 'popup-container-mobile';
			}else{
				class_name = 'bulkBins-container';
			}	
			this.dialog.open(AddClusterModal, { panelClass: class_name, data: {chosenBins : this.binsIncluded} });	
		}
	}

	manageBins(){
		if(this.binsIncluded.length > 1){
		  this.openManageModalForMulti(this.binsIncluded);
		}
	}

	openManageModalForMulti(bins){		
		let class_name = '';
		if(this.isMobile){
			class_name = 'popup-container-mobile';
		}else{
			class_name = 'bulkBins-container';
		}	
		this.dialog.open(ManageBinsModal, { panelClass: class_name , data: {chosenBins : bins} });
	}

	getPaths() {		
		if (this.polygon) {
		  const vertices = this.polygon.getPaths().getArray()[0];
		  let paths = [];
		  vertices.getArray().forEach(function (xy, i) {
			paths.push([xy.lat(),xy.lng()]);
		  });		  
		  return paths;
		}
		return [];
	}

	createCenterControl() {
		this.controlButton = document.createElement('img');	  
		this.controlButton.style.background = 'none padding-box rgb(255, 255, 255)';
		this.controlButton.style.position = 'relative';
		this.controlButton.style.cursor = 'pointer';
		this.controlButton.style.borderBottomRightRadius = '2px';
		this.controlButton.style.borderTopRightRadius = '2px';
		this.controlButton.style.width = '16px';
		this.controlButton.style.height = '100%';
		this.controlButton.src = 'assets/images/dashboard/mapSettings.svg';

		this.controlButton.addEventListener('click', () => {
			if(this.controlButton.src.includes("chevron")){
				document.getElementById('managePanelClicked').style.visibility = 'hidden';
				this.controlButton.src = 'assets/images/dashboard/mapSettings.svg';
			}else{
				document.getElementById('managePanelClicked').style.visibility = 'visible';
				this.controlButton.src = 'assets/images/dashboard/chevron.svg';
			}
		});
	  
		return this.controlButton;
	}

	closeAllInfoWindows = () => {
		this.infoWindows.forEach(iw => iw.close());
	};

	private wireMapInfoWindowCloseButton(iw: google.maps.InfoWindow): void {
		google.maps.event.addListener(iw, 'domready', () => {
			const closeBtn = document.querySelector('.gm-style-iw button.map-infowindow-close');
			closeBtn?.addEventListener('click', (e) => {
				e.stopPropagation();
				iw.close();
			});
		});
	}

	isPartOfCluster(clusters,elm){
		if(clusters[elm.cluster_id+elm["site_id"]].length > 1){
			return true;
		}else{
			return false;
		}
	}

	onBinInfoClick(bin: any) {
		let class_name = '';
		let selectedElements = [bin];
		if(this.isMobile){
			class_name = 'popup-container-mobile';
		}else{
			class_name = this.currentLang != "iw" ? "clusterInfo-container-ltr" : "clusterInfo-container-rtl";
		}

		// Check if dialog is already open (using static property for cross-page support)
		if(BinLiveInfoModal.openModalRef && BinLiveInfoModal.openModalRef.componentInstance && !BinLiveInfoModal.openModalRef.componentInstance.dialogRef.closed) {
			// Dialog is already open, add tabs to existing modal
			BinLiveInfoModal.openModalRef.componentInstance.addTabs(selectedElements);
			return;
		}

		const dialogRef = this.dialog.open(BinLiveInfoModal, {
			panelClass: class_name,
			width: '55%',
			data: {
			  item: selectedElements
			},
		});

		// Set static reference for cross-page access
		BinLiveInfoModal.openModalRef = dialogRef;

		// Clear reference when dialog is closed
		dialogRef.afterClosed().subscribe(() => {
			BinLiveInfoModal.openModalRef = null;
		});
	}

	private BinToType(status,mode): string {
		let icon = '';
		if(mode == "1"){
			icon = "Non Active";
		}else{
			if(status == "0"){
				icon = 'Green Bins';
			}else if(status == "1"){
				icon = 'Yellow Bins';
			}else if(status == "2"){
				icon = 'Red Bins';
			}
		}
		return icon;
	}

	private convertMarkerToColor(icon){
		if(icon.toLowerCase().includes("red")){
			return "Red Bins";
		}else if(icon.toLowerCase().includes("yellow")){
			return "Yellow Bins";
		}else if(icon.toLowerCase().includes("green")){
			return "Green Bins";
		}else{
			return "Non Active";
		}
	}

	private binToHtml(bin){
		let content = '';		
		if(!bin.cluster){
			let wazeLink = '';
			if(this.isMobile){
				wazeLink+= `<a style="order:2" class="tooltip-waze" href="https://waze.com/ul?ll=${bin.lat},${bin.lng}"><img class="wazeIcon" src="assets/images/dashboard/waze.svg" alt=""></a>`;				
			}
			content = `
			<div class="iw-content" style="padding-left:unset;padding-right:unset;">
			<div class="tooltip-id">
			<div style="display: flex;justify-content: space-between;align-items: center;padding-top: 12px;">
			<div class="tooltip-value">
			<a href="/binLiveInfoPage?pageName=dashboard&binId=${bin.bin_id}" class="binNameHref-truckMap tooltip-value bin-name">${bin.bin_name}</a>
			<span> | </span>
			<span>${bin.bin_id}</span>
			</div>
			<div style="display: flex;align-items: center;gap: 12px;">
				${!this.isMobile ? `<div class="${this.currentLang == 'iw' ? 'dropstart' : ''}">
					<div class="dropdown-toggle map-infowindow-dots" data-bs-toggle="dropdown" style="cursor: pointer;display: flex;">
						<img alt="three-dots" src="assets/images/dashboard/threedots.svg">
					</div>						
					<div class="dropdown-menu map-infowindow-dropdown ${this.currentLang == 'iw' ? 'rtl' : ''}">     
						<div style="color: var(--Dark-Green-800, #485760); font-family: Open Sans, sans-serif; font-size: 12px; font-style: normal; font-weight: 400; display: flex; padding: 4px 0px;">
							<span id="bin-action-btn" style="cursor: pointer;">${this.translationsObj.MAP_MARKER.BIN_INFO}</span>
						</div>
						${(this.user_role_id == 1 || this.user_role_id == 2 || this.user_role_id == 4 || this.user_role_id == 5 || this.user_role_id == 6) && bin.lat != 0 && bin.lng != 0 ? 
						`<div style="color: var(--Dark-Green-800, #485760); font-family: Open Sans, sans-serif; font-size: 12px; font-style: normal; font-weight: 400; display: flex; padding: 4px 0px;">
							<span id="click-future-workplan" style="cursor: pointer;">${this.translationsObj.WORKPLAN.ADD_BIN_TO_FUTURE_WORKPLAN}</span>
						</div>`	: ``}
					</div>
				</div>` : ''}
				<div class="map-infowindow-close-row" style="display:flex;">
					<button type="button" class="map-infowindow-close" style="border: none;background: transparent;padding: unset;display: flex;">
						<img src="assets/images/dashboard/close_infoWindow.svg" alt=""/>
					</button>
				</div>
			</div>	
			</div>
			<div class="tooltip-id border-bottom">
			${wazeLink}
			<div class="tooltip-address" title="${bin.bin_address}">${bin.bin_address}</div>
			<div class="tooltip-address" title="${bin.bin_neighbourhood == null ? '' : bin.bin_neighbourhood}">${bin.bin_neighbourhood == null ? '' : bin.bin_neighbourhood}</div>
			</div>
			</div>
			</div>
			<div class="tooltip-id" style="padding-bottom: 9px;">
			<div class="tooltip-text">${this.usageType == 1 ? this.translationsObj.MAP_MARKER.VOLUME : this.translationsObj.MAP_MARKER.VOLUME_D}:</div>
			</div>          
			<div class="tooltip-marker" style="${this.getTooltipStyle(bin.fill_level_percent)}">
			${this.usageType == 1 ? bin.fill_level_percent : bin.bin_max_capacity_in_liters * bin.fill_level_percent / 100}${this.capacityDisplayType !=0 ? 'L' : '%'}
			<div class="tooltip-arrow-marker"></div>
			</div>
			
			<div class="progress_bar" id="progressbar" style="background: ${this.getProgressBarStyleWrapper(bin.color_status_name)};border-radius: 10px;margin-bottom: 12px;display:flex;position: relative;">
			<div class="progress_bar_internal" style="width: ${this.getProgressBarWidthInternal(bin.fill_level_percent)};
			height: 14px;background: ${this.getProgressBarStyleInternal(bin.color_status_name)};border-radius: 10px; line-height: 15px;">
			</div>
			</div>	
			<div class="last">
			<div class="tooltip-id">
			<div class="tooltip-text-collection">${this.usageType == 1 ? this.translationsObj.LIST_COLUMNS.LAST_COLLECTION : this.translationsObj.LIST_COLUMNS.LAST_REFILL}:&nbsp;</div>		
			<div class="tooltip-text-collection">${(this.currentLang == 'iw' && bin.last_collection_time!='' && bin.last_collection_time!=undefined ? this.utilService.convertDateHebrew(bin.last_collection_time) : this.utilService.convertDateEnglish(bin.last_collection_time))}</div>
			<div class="tooltip-text-collection">${(bin.last_collection_time=='' || bin.last_collection_time==undefined) && this.usageType == 1 ? this.translationsObj.TITLES.LAST_COL : ((bin.last_collection_time=='' || bin.last_collection_time==undefined) && this.usageType != 1 ? this.translationsObj.TITLES.LAST_REFILL : '')}</div>
			</div>
			</div>`;
		}else{						
			let binTooltips = '';
			let wazeLink = '';
			for(let i=0; i < bin.item.length; i++){
				let capacityVolume = bin.item[i].Bin_live_Data.fill_level_percent;
				let capacityDataSymbol = '%';
				if (this.capacityDisplayType != 0) {
					capacityDataSymbol = 'L';
					capacityVolume = bin.item[i].Bin_thresholds.bin_max_capacity_in_liters * capacityVolume / 100;
				}
				if(this.isMobile){
					wazeLink += `<a class="tooltip-waze" href="https://waze.com/ul?ll=${bin.lat},${bin.lng}"><img class="wazeIcon" src="assets/images/dashboard/waze.svg" alt=""></a>`;
				}

				binTooltips += `
				<div class="carousel-item ${i == 0 ? "active" : ""}">
				<div class="iw-content" style="padding-left:unset;padding-right:unset;">
				<div class="tooltip-id">
				<div class="tooltip-value">
				<a class="binNameHref-truckMap tooltip-value bin-name" href="/binLiveInfoPage?pageName=workplans&binId=${bin.item[i].bin_id}" id="clickableItem">${bin.item[i].bin_name}</a>
				<span> | </span>
				<span>${bin.item[i].bin_id}</span>
				</div>
				<div class="tooltip-id border-bottom">
				${wazeLink}
				<div class="tooltip-address">${bin.item[i].Bin_location.bin_address}</div>
				<div class="tooltip-address">${bin.item[i].Bin_location.bin_neighbourhood == null ? "" : bin.item[i].Bin_location.bin_neighbourhood}</div>
				</div>
				</div>
				</div>
				<div class="tooltip-id" style="padding-bottom: 9px;">
				<div class="tooltip-text">${this.translationsObj.MAP_MARKER.VOLUME}:
				</div>
				</div>
				<div class="tooltip-marker" style="${this.getTooltipStyle(bin.item[i].Bin_live_Data.fill_level_percent)}">
				${this.usageType == 1 ? bin.item[i].Bin_live_Data.fill_level_percent : bin.item[i].Bin_thresholds.bin_max_capacity_in_liters * bin.item[i].Bin_live_Data.fill_level_percent / 100}${this.capacityDisplayType !=0 ? 'L' : '%'}
				<div class="tooltip-arrow-marker"></div>
				</div>    
				<div id="progressbar" class="progress_bar" style="background: ${this.getProgressBarStyleWrapper(bin.item[i].Bin_live_Data.color_status_name)};border-radius: 10px;margin-bottom: 5px;display:flex;position: relative;">
				<div class="progress_bar_internal" style="background: ${this.getProgressBarStyleInternal(bin.item[i].Bin_live_Data.color_status_name)};width: ${this.getProgressBarWidthInternal(bin.item[i].Bin_live_Data.fill_level_percent)};height: 14px;border-radius: 10px; line-height: 15px;">
				</div>
				</div>
				<div class="last">
				<div class="tooltip-id">
				<div class="tooltip-text-collection">
				${this.usageType == 1 ? this.translationsObj.LIST_COLUMNS.LAST_COLLECTION : ''}:
				</div>
				<div class="tooltip-text-collection">${this.usageType == 1 ? (this.currentLang == 'iw' && bin.item[i].Bin_live_Data.last_collection_time!='' && bin.item[i].Bin_live_Data.last_collection_time!=undefined ? this.utilService.convertDateHebrew(bin.item[i].Bin_live_Data.last_collection_time) : this.utilService.convertDateEnglish(bin.item[i].Bin_live_Data.last_collection_time)) : ''}</div>
				<div class="tooltip-text-collection">${(bin.item[i].Bin_live_Data.last_collection_time=='' || bin.item[i].Bin_live_Data.last_collection_time==undefined) && this.usageType == 1 ? this.translationsObj.TITLES.LAST_COL : ''}</div>				
				</div>
				</div>
				</div>
				`;
			}
			content = `
			<div style="display: flex;justify-content: space-between;align-items: center;padding-top: 12px;">
			<div style="display:flex;align-items:center;">
			<button class="carousel-control" type="button" data-bs-target="#carouselExampleControls" data-bs-slide="prev" class="prevNextArrow">
			<img src="assets/images/dashboard/pagination_arrow.png" alt="" class="${this.currentLang != 'iw' ? "rotation" : ""}">
			</button>
			<span style="position:relative;bottom:3px;padding: 0px 8px;">|</span>
			<button class="carousel-control" type="button" data-bs-target="#carouselExampleControls" data-bs-slide="next">
			<img src="assets/images/dashboard/pagination_arrow.png" alt="" class="${this.currentLang == 'iw' ? "rotation" : ""}">
			</button>
			</div>
			<div style="display: flex;align-items: center;gap: 12px;">
				${(this.user_role_id == 1 || this.user_role_id == 2 || this.user_role_id == 4 || this.user_role_id == 5 || this.user_role_id == 6) && bin.lat != 0 && bin.lng != 0 && !this.isMobile ? 
					`<div class="${this.currentLang == 'iw' ? 'dropstart' : ''}">
					<div class="dropdown-toggle map-infowindow-dots" data-bs-toggle="dropdown" style="cursor: pointer;display: flex;">
						<img alt="three-dots" src="assets/images/dashboard/threedots.svg">
					</div>						
					<div class="dropdown-menu map-infowindow-dropdown ${this.currentLang == 'iw' ? 'rtl' : ''}">     
						<div style="color: var(--Dark-Green-800, #485760); font-family: Open Sans, sans-serif; font-size: 12px; font-style: normal; font-weight: 400; display: flex; padding: 4px 0px;">
							<span id="click-future-workplan" style="cursor: pointer;">${this.translationsObj.WORKPLAN.ADD_BIN_TO_FUTURE_WORKPLAN}</span>
						</div>
					</div>
				</div>`	: ``}
				<div class="map-infowindow-close-row" style="display:flex;">
					<button type="button" class="map-infowindow-close" style="border: none;background: transparent;padding: unset;display: flex;">
						<img src="assets/images/dashboard/close_infoWindow.svg" alt=""/>
					</button>
				</div>
			</div>	
			</div>
			<div id="carouselExampleControls" style="top:9px" class="carousel slide" data-bs-touch="false" data-bs-interval="false">
			<div class="carousel-inner">			
				${binTooltips}
			</div>`;
		}
		return content;
	}

	getProgressBarStyleWrapper = (binInfo: any) => {		
		if (!binInfo) { return ''; }
		if (binInfo == "GREEN") {
			return 'rgba(52, 208, 141, 0.15)';
		} else if (binInfo == "YELLOW") {
			return 'rgba(226, 224, 85, 0.15)';
		}
		return 'rgba(215, 89, 76, 0.15)';
  	}

	getProgressBarWidthInternal = (binInfo: any) => {
		if (!binInfo) { return ''; }
		return `${binInfo}%`;
	}

	getProgressBarStyleInternal = (binInfo: any) => {
		if (!binInfo) { return ''; }
		if (binInfo == "GREEN") {
			return '#34D08D';
		} else if (binInfo == "YELLOW") {
			return '#E2E055';
		}
		return '#D7594C';
	}

	getTooltipStyle = (binInfo: any) => {		
		if(this.currentLang == 'iw'){
			return `margin-right: ${binInfo-8}%`;
		}else{
			return `margin-left: ${binInfo-8}%`;
		}
	}

	private labelOption(bin,icon){			
		let label = {
			text: bin.length.toString(),
			className: "marker-label",
			color: this.convertColotToInt(icon),
			fontSize: "16px",
			fontWeight: "bold"
		}
		return label;
	}

	private convertColotToInt = (icon) => {
		if(icon.toString().includes('red')){
			return "#E01B09";
		}else if(icon.toString().includes('yellow')){
			return "#FAB300";
		}else if(icon.toString().includes('green')){
			return "#245B3E";
		}else{
			return "#68747A";
		}
	}

	closeCMP(){
		this.showCMP.emit(false);
	}

	applyFilter = (filterValue) => {
		filterValue = filterValue.value.trim().toLowerCase();

		if(filterValue == ""){
			setTimeout(() => {
				this.clusterer.clearMarkers();
				this.clusterer.addMarkers(this.backUpMarkers);
	
				const bounds = new google.maps.LatLngBounds();
				this.backUpMarkers.forEach((m) => bounds.extend(m.getPosition()!));
				this.mapLiveView.fitBounds(bounds);
				return;
			}, 0);
		}

		const filteredMarkers = this.backUpMarkers.filter((m) => {
			return this.matchesFilter(m, filterValue);
		});
	
		this.clusterer.clearMarkers();
		this.clusterer.addMarkers(filteredMarkers);

		if (filteredMarkers.length > 0) {
			const bounds = new google.maps.LatLngBounds();
			filteredMarkers.forEach((m) => {
			  bounds.extend(m.getPosition()!);
			});
			this.mapLiveView.fitBounds(bounds);
		}
	}

	matchesFilter = (item: any, filterValue: string) => {
		if (!filterValue) return true; // if no search, match all
		const value = filterValue.toLowerCase();
	  
		return (
		  item["bin_id"]?.toString().toLowerCase().includes(value) ||
		  item["bin_name"]?.toString().toLowerCase().includes(value) ||
		  (item["Bin_location"]?.["bin_neighbourhood"] &&
			item["Bin_location"]["bin_neighbourhood"]
			  .toString()
			  .toLowerCase()
			  .includes(value)) ||
		  item["Bin_location"]?.["bin_address"]
			?.toString()
			.toLowerCase()
			.includes(value)
		);
	};

	showClustersbtn = () => {
		if(this.isDisabled){return;}
		this.showClusters = true;	
		this.filterClustersAndColors();	
	}

	hideClustersbtn = () => {
		if(this.isDisabled){return;}
		this.showClusters = false;	
		this.filterClustersAndColors();	
	}

	filterClustersAndColors(){	
		if(!this.showClusters){	
			this.fliterMarkers();
		}else if(this.showClusters){
			this.filterClusters();
		}
	}

	fliterMarkers() {	
		const filteredMarkers = this.backUpMarkers.filter((m) => {
			return this.types.includes(m["category"]);
		});

		this.clusterer.clearMarkers();
		this.clusterer.addMarkers(filteredMarkers);
	}

	filterClusters(){	
		const filteredMarkers = this.backUpMarkers.filter((m) => {
			return (m["partOfCluster"] && m["cluster"]) || m["nearbyBin"];
		});
		
		this.clusterer.clearMarkers();
		this.clusterer.addMarkers(filteredMarkers);
	}
	
	selectUnselectItem(item) {
		this.closeAllInfoWindows();
		item.selected = !item.selected;
		this.types = this.items.filter(item => item.selected).map(item => item.name);
		if(this.types.length != 4){
			this.hideClustersbtn();
			this.isDisabled = true;
		}else{
			this.isDisabled = false;
		}
		this.filterClustersAndColors();
	}

	handleResetClicked = (reset) => {
		this.closeAllInfoWindows();
		_.each(this.neighborhoodsPolygon, (polygon) => {
			polygon.setMap(null);
		});

		_.each(this.FullClustersPolygon, (polygon) => {
			polygon.setMap(null);	
		});

		_.each(this.SemiClustersPolygon, (polygon) => {
			polygon.setMap(null);
		});
	}

	handleClusterClicked = (cluster) => {
		if(cluster){
			this.showClustersbtn();
		}else{
			this.hideClustersbtn();
		}
	}

	handleNeighborhoodsSelected = (neighborhood) => {
		let coordinates = [];
		let polygonBounds = [];
		let collectedBins = [];
		let avgFillLevel = 0;
		let collectionCoverage = 0;
		if(neighborhood.selected){
			const filteredBins = _.filter(this.backUpBins, (bin) => {
				return neighborhood.value.nbrh_name == bin.Bin_location["bin_neighbourhood"];
			});
			_.each(filteredBins, (bin) => {
				if(moment(moment(bin.Bin_live_Data.last_collection_time).format('YYYY-MM-DD')).isBetween(moment().startOf('month').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD'))){
					collectedBins.push(bin);
				}
				if(bin.average_daily_fill_rate_in_percent){
					avgFillLevel += bin.average_daily_fill_rate_in_percent;
				}
				coordinates.push([bin["Bin_location"]["bin_latitude"],bin["Bin_location"]["bin_longitude"]]);
			});

			avgFillLevel = Number(Number(avgFillLevel / filteredBins.length).toFixed(0));
			collectionCoverage = Number(Number((collectedBins.length / filteredBins.length) * 100).toFixed(0));
			const progress = avgFillLevel / 100;
			const dashoffset = CIRCUMFERENCE * (1 - progress);	
			if(filteredBins.length > 2){
				const grahamScan = new GrahamScan();
				grahamScan.setPoints(coordinates);
				const hull = grahamScan.getHull();

				_.each(hull, (boundary) => {
					polygonBounds.push({lat : boundary[0],lng : boundary[1]});
				});
			}else{
				_.each(coordinates, (boundary) => {
					polygonBounds.push({lat : boundary[0],lng : boundary[1]});
				});
			}
	
			const neighborhoodsPolygon = new google.maps.Polygon({
				paths: polygonBounds,
				strokeColor: "#8750FC",
				strokeWeight: 2,
				fillColor: "#8750fc",
			});
			neighborhoodsPolygon["id"] = neighborhood.value.nbrh_name;
			neighborhoodsPolygon["binsIncluded"] = neighborhood.value.num_of_bins_in_nbrh;
			neighborhoodsPolygon["avgFillLevel"] = avgFillLevel;
			neighborhoodsPolygon["collectionCoverage"] = collectionCoverage;
			neighborhoodsPolygon.setMap(this.mapLiveView);
			this.neighborhoodsPolygon.push(neighborhoodsPolygon);

			var bounds = new google.maps.LatLngBounds();
			neighborhoodsPolygon.getPath().forEach(function(path, index) {
				bounds.extend(path);
			});
			this.mapLiveView.fitBounds(bounds);
			neighborhoodsPolygon.addListener("click", (event) => {
				this.binsIncluded = [...filteredBins];
				this.closeAllInfoWindows();
				if(this.infoWindow){
					this.infoWindow.close();
				}
				this.infoWindow = new google.maps.InfoWindow(mapInfoWindowOptions);
				this.wireMapInfoWindowCloseButton(this.infoWindow);
				let contentString = `
				<div>
					<div style="display: flex;justify-content: space-between;align-items: center;padding: 16px 0px 10px 0px;border-bottom: 1px solid #CDD2D4;">
						<div style="font-family: Open Sans, sans-serif;display: flex;gap: 4px;font-style: normal;color: #2A3338;align-items: center;">
							<span style="font-weight: 700;">${neighborhoodsPolygon["id"]}</span>
							<span> | </span>
							<span style="font-weight: 600;">${this.translationsObj.TITLES.TOTAL_BINS}</span>
							<span style="font-weight: 600;">(${neighborhoodsPolygon["binsIncluded"]})</span>
						</div>
						<div style="display: flex;align-items: center;gap: 12px;">
							${(this.user_role_id == 1 || this.user_role_id == 2 || this.user_role_id == 4 || this.user_role_id == 5 || this.user_role_id == 6) && !this.isMobile ?
								`<div>
								<div class="dropdown-toggle map-infowindow-dots" data-bs-toggle="dropdown" style="cursor: pointer;display: flex;">
									<img alt="three-dots" src="assets/images/dashboard/threedots.svg">
								</div>						
								<div class="dropdown-menu map-infowindow-dropdown ${this.currentLang == 'iw' ? 'rtl' : ''}">     
									<div style="color: var(--Dark-Green-800, #485760); font-family: Open Sans, sans-serif; font-size: 12px; font-style: normal; font-weight: 400; display: flex; padding: 4px 0px;">
										<span id="click-future-workplan" style="cursor: pointer;">${this.translationsObj.WORKPLAN.ADD_BIN_TO_FUTURE_WORKPLAN}</span>
									</div>
								</div>
							</div>`	: ``}
							<div class="map-infowindow-close-row" style="display:flex;">
								<button type="button" class="map-infowindow-close" style="border: none;background: transparent;padding: unset;display: flex;">
									<img src="assets/images/dashboard/close_infoWindow.svg" alt=""/>
								</button>
							</div>
						</div>	
					</div>
					<div style="padding-top: 10px;">
						<div style="display: flex;gap: 12px;align-items: center;">
							<img src="assets/images/dashboard/greenBin.svg" alt=""/>
							<div style="display: flex;gap: 4px;">
								<span style="font-family: Open Sans, sans-serif;font-style: normal;font-weight: 600; font-size: 14px;color:#2A3338;align-items: center;display: flex">${neighborhoodsPolygon["collectionCoverage"]}%</span>
								<span style="font-family: Open Sans, sans-serif;font-style: normal;font-weight: 400; font-size: 12px;color:#2A3338;">${this.translationsObj.TITLES.COLLECTED_BINS}</span>
							</div>
						</div>
						<div style="display: flex;gap: 12px;padding-top: 20px;align-items: center;">
							<div class="circle">
								<svg style="transform: rotate(-90deg)" width="24" height="24" viewBox="0 0 120 120">
									<circle style="fill: none;stroke: #CDD2D4" class="progress__meter" cx="60" cy="60" r="55" stroke-width="10" />
									<circle style="stroke-dashoffset:${dashoffset};stroke-dasharray:${CIRCUMFERENCE};fill: none;stroke: #28B365;stroke-linecap: round" class="progress__value averageCapacity" cx="60" cy="60" r="55" stroke-width="10" />
								</svg>
							</div>
							<div style="display: flex;gap: 4px;">
								<span style="font-family: Open Sans, sans-serif;font-style: normal;font-weight: 600; font-size: 14px;color:#2A3338;">${neighborhoodsPolygon["avgFillLevel"]}%</span>
								<span style="font-family: Open Sans, sans-serif;font-style: normal;font-weight: 400; font-size: 12px;color:#2A3338;">${this.translationsObj.LIVE_INFO.AVG_RATE}</span>
							</div>
						</div>
					</div>
				</div>`;
				this.infoWindow.setContent(contentString);
				this.infoWindow.setPosition(event.latLng);
				this.infoWindow.open(this.mapLiveView);
			});
		}else{
			_.each(this.neighborhoodsPolygon, (polygon) => {
				if(polygon["id"] == neighborhood.value.nbrh_name){
					polygon.setMap(null);
				}
			});
		}
	}

	handleFullClusterSelection = (fullCluster) => {
		let coordinates = [];
		let polygonBounds = [];
		let collectedBins = [];
		let avgFillLevel = 0;
		let collectionCoverage = 0;
		if(fullCluster.selected){
			const filteredBins = _.filter(this.backUpBins, (bin) => {
				return fullCluster.value.cluster_id == bin.cluster_id;
			});
			_.each(filteredBins, (bin) => {
				if(moment(moment(bin.Bin_live_Data.last_collection_time).format('YYYY-MM-DD')).isBetween(moment().startOf('month').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD'))){
					collectedBins.push(bin);
				}
				if(bin.average_daily_fill_rate_in_percent){
					avgFillLevel += bin.average_daily_fill_rate_in_percent;
				}
				coordinates.push([bin["Bin_location"]["bin_latitude"],bin["Bin_location"]["bin_longitude"]]);
			});

			avgFillLevel = Number(Number(avgFillLevel / filteredBins.length).toFixed(0));
			collectionCoverage = Number(Number((collectedBins.length / filteredBins.length) * 100).toFixed(0));

			const progress = avgFillLevel / 100;
			const dashoffset = CIRCUMFERENCE * (1 - progress);	

			if(filteredBins.length > 2){
				const grahamScan = new GrahamScan();
				grahamScan.setPoints(coordinates);
				const hull = grahamScan.getHull();

				_.each(hull, (boundary) => {
					polygonBounds.push({lat : boundary[0],lng : boundary[1]});
				});
			}else{
				_.each(coordinates, (boundary) => {
					polygonBounds.push({lat : boundary[0],lng : boundary[1]});
				});
			}
	
			const FullClusterPolygon = new google.maps.Polygon({
				paths: polygonBounds,
				strokeColor: "#64A5F2",
				strokeWeight: 2,
				fillColor: "#006eef",
			});
			FullClusterPolygon["id"] = fullCluster.value.cluster_id;
			FullClusterPolygon["binsIncluded"] = fullCluster.value.num_of_bins_in_cluster;
			FullClusterPolygon["avgFillLevel"] = avgFillLevel;
			FullClusterPolygon["collectionCoverage"] = collectionCoverage;
			FullClusterPolygon.setMap(this.mapLiveView);
			this.FullClustersPolygon.push(FullClusterPolygon);

			var bounds = new google.maps.LatLngBounds();
			FullClusterPolygon.getPath().forEach(function(path, index) {
				bounds.extend(path);
			});
			this.mapLiveView.fitBounds(bounds);
			FullClusterPolygon.addListener("click", (event) => {
				this.binsIncluded = [...filteredBins];
				this.closeAllInfoWindows();
				if(this.infoWindow){
					this.infoWindow.close();
				}
				this.infoWindow = new google.maps.InfoWindow(mapInfoWindowOptions);
				this.wireMapInfoWindowCloseButton(this.infoWindow);
				let contentString = `
				<div>
					<div style="display: flex;justify-content: space-between;align-items: center;padding: 16px 0px 10px 0px;border-bottom: 1px solid #CDD2D4;">
						<div style="font-family: Open Sans, sans-serif;display: flex;gap: 4px;font-style: normal;color: #2A3338;">
							<span style="font-weight: 700;">${this.translationsObj.SITE_MANAGMENT.CLUSTER} ${FullClusterPolygon["id"]}</span>
							<span> | </span>
							<span style="font-weight: 600;">${this.translationsObj.TITLES.TOTAL_BINS}</span>
							<span style="font-weight: 600;">(${FullClusterPolygon["binsIncluded"]})</span>
						</div>
						<div style="display: flex;align-items: center;gap: 12px;">
							${(this.user_role_id == 1 || this.user_role_id == 2 || this.user_role_id == 4 || this.user_role_id == 5 || this.user_role_id == 6) && !this.isMobile ?
								`<div>
								<div class="dropdown-toggle map-infowindow-dots" data-bs-toggle="dropdown" style="cursor: pointer;display: flex;">
									<img alt="three-dots" src="assets/images/dashboard/threedots.svg">
								</div>						
								<div class="dropdown-menu map-infowindow-dropdown ${this.currentLang == 'iw' ? 'rtl' : ''}">     
									<div style="color: var(--Dark-Green-800, #485760); font-family: Open Sans, sans-serif; font-size: 12px; font-style: normal; font-weight: 400; display: flex; padding: 4px 0px;">
										<span id="click-future-workplan" style="cursor: pointer;">${this.translationsObj.WORKPLAN.ADD_BIN_TO_FUTURE_WORKPLAN}</span>
									</div>
								</div>
							</div>`	: ``}
							<div class="map-infowindow-close-row" style="display:flex;">
								<button type="button" class="map-infowindow-close" style="border: none;background: transparent;padding: unset;display: flex;">
									<img src="assets/images/dashboard/close_infoWindow.svg" alt=""/>
								</button>
							</div>
						</div>	
					</div>
					<div style="padding-top: 10px;">
						<div style="display: flex;gap: 12px;align-items: center;">
							<img src="assets/images/dashboard/greenBin.svg" alt=""/>
							<div style="display: flex;gap: 4px;">
								<span style="font-family: Open Sans, sans-serif;font-style: normal;font-weight: 600; font-size: 14px;color:#2A3338;align-items: center;display: flex">${FullClusterPolygon["collectionCoverage"]}%</span>
								<span style="font-family: Open Sans, sans-serif;font-style: normal;font-weight: 400; font-size: 12px;color:#2A3338;">${this.translationsObj.TITLES.COLLECTED_BINS}</span>
							</div>
						</div>
						<div style="display: flex;gap: 12px;padding-top: 20px;align-items: center;">
							<div class="circle">
								<svg style="transform: rotate(-90deg)" width="24" height="24" viewBox="0 0 120 120">
									<circle style="fill: none;stroke: #CDD2D4" class="progress__meter" cx="60" cy="60" r="55" stroke-width="10" />
									<circle style="stroke-dashoffset:${dashoffset};stroke-dasharray:${CIRCUMFERENCE};fill: none;stroke: #28B365;stroke-linecap: round" class="progress__value averageCapacity" cx="60" cy="60" r="55" stroke-width="10" />
								</svg>
							</div>
							<div style="display: flex;gap: 4px;">
								<span style="font-family: Open Sans, sans-serif;font-style: normal;font-weight: 600; font-size: 14px;color:#2A3338;">${FullClusterPolygon["avgFillLevel"]}%</span>
								<span style="font-family: Open Sans, sans-serif;font-style: normal;font-weight: 400; font-size: 12px;color:#2A3338;">${this.translationsObj.LIVE_INFO.AVG_RATE}</span>
							</div>
						</div>
					</div>
				</div>`;
				this.infoWindow.setContent(contentString);
				this.infoWindow.setPosition(event.latLng);
				this.infoWindow.open(this.mapLiveView);
			});
		}else{
			_.each(this.FullClustersPolygon, (polygon) => {
				if(polygon["id"] == fullCluster.value.cluster_id){
					polygon.setMap(null);
				}
			});
		}
	}

	handleSemiClusterSelection = (semiCluster) => {
		let coordinates = [];
		let polygonBounds = [];
		let collectedBins = [];
		let avgFillLevel = 0;
		let collectionCoverage = 0;
		if(semiCluster.selected){
			const filteredBins = _.filter(this.backUpBins, (bin) => {
				return semiCluster.value.cluster_id == bin.cluster_id;
			});
			_.each(filteredBins, (bin) => {
				if(moment(moment(bin.Bin_live_Data.last_collection_time).format('YYYY-MM-DD')).isBetween(moment().startOf('month').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD'))){
					collectedBins.push(bin);
				}
				if(bin.average_daily_fill_rate_in_percent){
					avgFillLevel += bin.average_daily_fill_rate_in_percent;
				}
				coordinates.push([bin["Bin_location"]["bin_latitude"],bin["Bin_location"]["bin_longitude"]]);
			});

			avgFillLevel = Number(Number(avgFillLevel / filteredBins.length).toFixed(0));
			collectionCoverage = Number(Number((collectedBins.length / filteredBins.length) * 100).toFixed(0));
			const progress = avgFillLevel / 100;
			const dashoffset = CIRCUMFERENCE * (1 - progress);	

			if(filteredBins.length > 2){
				const grahamScan = new GrahamScan();
				grahamScan.setPoints(coordinates);
				const hull = grahamScan.getHull();

				_.each(hull, (boundary) => {
					polygonBounds.push({lat : boundary[0],lng : boundary[1]});
				});
			}else{
				_.each(coordinates, (boundary) => {
					polygonBounds.push({lat : boundary[0],lng : boundary[1]});
				});
			}
	
			const SemiClusterPolygon = new google.maps.Polygon({
				paths: polygonBounds,
				strokeColor: "#006EEF",
				strokeWeight: 2,
				fillColor: "#006eef",
			});
			SemiClusterPolygon["id"] = semiCluster.value.cluster_id;
			SemiClusterPolygon["binsIncluded"] = semiCluster.value.num_of_bins_in_cluster;
			SemiClusterPolygon["avgFillLevel"] = avgFillLevel;
			SemiClusterPolygon["collectionCoverage"] = collectionCoverage;
			SemiClusterPolygon.setMap(this.mapLiveView);
			this.SemiClustersPolygon.push(SemiClusterPolygon);

			var bounds = new google.maps.LatLngBounds();
			SemiClusterPolygon.getPath().forEach(function(path, index) {
				bounds.extend(path);
			});
			this.mapLiveView.fitBounds(bounds);
			SemiClusterPolygon.addListener("click", (event) => {
				this.binsIncluded = [...filteredBins];
				this.closeAllInfoWindows();
				if(this.infoWindow){
					this.infoWindow.close();
				}
				this.infoWindow = new google.maps.InfoWindow(mapInfoWindowOptions);
				this.wireMapInfoWindowCloseButton(this.infoWindow);
				let contentString = `
				<div>
					<div style="display: flex;justify-content: space-between;align-items: center;padding: 16px 0px 10px 0px;border-bottom: 1px solid #CDD2D4;">
						<div style="font-family: Open Sans, sans-serif;display: flex;gap: 4px;font-style: normal;color: #2A3338;">
							<span style="font-weight: 700;">${SemiClusterPolygon["id"]}</span>
							<span> | </span>
							<span style="font-weight: 600;">${this.translationsObj.TITLES.TOTAL_BINS}</span>
							<span style="font-weight: 600;">(${SemiClusterPolygon["binsIncluded"]})</span>
						</div>
						<div style="display: flex;align-items: center;gap: 12px;">
							${(this.user_role_id == 1 || this.user_role_id == 2 || this.user_role_id == 4 || this.user_role_id == 5 || this.user_role_id == 6) && !this.isMobile ?
								`<div>
								<div class="dropdown-toggle map-infowindow-dots" data-bs-toggle="dropdown" style="cursor: pointer;display: flex;">
									<img alt="three-dots" src="assets/images/dashboard/threedots.svg">
								</div>						
								<div class="dropdown-menu map-infowindow-dropdown ${this.currentLang == 'iw' ? 'rtl' : ''}">     
									<div style="color: var(--Dark-Green-800, #485760); font-family: Open Sans, sans-serif; font-size: 12px; font-style: normal; font-weight: 400; display: flex; padding: 4px 0px;">
										<span id="click-future-workplan" style="cursor: pointer;">${this.translationsObj.WORKPLAN.ADD_BIN_TO_FUTURE_WORKPLAN}</span>
									</div>
								</div>
							</div>`	: ``}
							<div class="map-infowindow-close-row" style="display:flex;">
								<button type="button" class="map-infowindow-close" style="border: none;background: transparent;padding: unset;display: flex;">
									<img src="assets/images/dashboard/close_infoWindow.svg" alt=""/>
								</button>
							</div>
						</div>			
					</div>
					<div style="padding-top: 10px;">
						<div style="display: flex;gap: 12px;align-items: center;">
							<img src="assets/images/dashboard/greenBin.svg" alt=""/>
							<div style="display: flex;gap: 4px;">
								<span style="font-family: Open Sans, sans-serif;font-style: normal;font-weight: 600; font-size: 14px;color:#2A3338;align-items: center;display: flex">${SemiClusterPolygon["collectionCoverage"]}%</span>
								<span style="font-family: Open Sans, sans-serif;font-style: normal;font-weight: 400; font-size: 12px;color:#2A3338;">${this.translationsObj.TITLES.COLLECTED_BINS}</span>
							</div>
						</div>
						<div style="display: flex;gap: 12px;padding-top: 20px;align-items: center;">
							<div class="circle">
								<svg style="transform: rotate(-90deg)" width="24" height="24" viewBox="0 0 120 120">
									<circle style="fill: none;stroke: #CDD2D4" class="progress__meter" cx="60" cy="60" r="55" stroke-width="10" />
									<circle style="stroke-dashoffset:${dashoffset};stroke-dasharray:${CIRCUMFERENCE};fill: none;stroke: #28B365;stroke-linecap: round" class="progress__value averageCapacity" cx="60" cy="60" r="55" stroke-width="10" />
								</svg>
							</div>
							<div style="display: flex;gap: 4px;">
								<span style="font-family: Open Sans, sans-serif;font-style: normal;font-weight: 600; font-size: 14px;color:#2A3338;">${SemiClusterPolygon["avgFillLevel"]}%</span>
								<span style="font-family: Open Sans, sans-serif;font-style: normal;font-weight: 400; font-size: 12px;color:#2A3338;">${this.translationsObj.LIVE_INFO.AVG_RATE}</span>
							</div>
						</div>
					</div>
				</div>`;
				this.infoWindow.setContent(contentString);
				this.infoWindow.setPosition(event.latLng);
				this.infoWindow.open(this.mapLiveView);
			});
		}else{
			_.each(this.SemiClustersPolygon, (polygon) => {
				if(polygon["id"] == semiCluster.value.cluster_id){
					polygon.setMap(null);
				}
			});
		}
	}
	
	private clearMarkersAndClusters() {
		if (this.clusterer) {
			this.clusterer.clearMarkers(); // removes markers from clusterer
			this.clusterer.setMap(null);   // detach clusterer from map
			this.clusterer = null;
		}
	
		if (this.markers) {
			for (const marker of this.markers) {
				marker.setMap(null); // remove from map
			}
			this.markers.length = 0; // fast clear array
		}		
	}

	private distance(lat1: number,lon1: number,lat2: number,lon2: number,unit?: string): number {
		const toRad = (deg: number) => (deg * Math.PI) / 180;
		const R = 6_371_000; // meters
	  
		const φ1 = toRad(lat1);
		const φ2 = toRad(lat2);
		const Δφ = toRad(lat2 - lat1);
		const Δλ = toRad(lon2 - lon1);
	  
		const a =
		  Math.sin(Δφ / 2) ** 2 +
		  Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
	  
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		const meters = R * c;
		if (unit == "M") return meters;
		if (unit == "K") return meters / 1000;
		return meters / 1609.344; // miles
	  }
	  
	  sortBinsAccordingToCoordinates(bins: API.Bin[]) {
		const bySite = new Map<string | number, API.Bin[]>();
		for (const b of bins) {
		  const key = (b as any).site_id; // adjust typing if needed
		  if (!bySite.has(key)) bySite.set(key, []);
		  bySite.get(key)!.push(b);
		}
	  
		const nearby = new Set<string | number>();
	  
		for (const group of bySite.values()) {
		  for (let i = 0; i < group.length; i++) {
			const bi = group[i];
			const { bin_latitude: lat1, bin_longitude: lon1 } = bi.Bin_location;
	  
			for (let j = i + 1; j < group.length; j++) {
			  const bj = group[j];
			  const { bin_latitude: lat2, bin_longitude: lon2 } = bj.Bin_location;
	  
			  const d = this.distance(lat1, lon1, lat2, lon2, "M");
			  if (d <= 10) {
				nearby.add((bi as any).bin_id);
				nearby.add((bj as any).bin_id);
			  }
			}
		  }
		}
	  
		return bins.map((b) => ({
		  ...b,
		  nearbyBin: nearby.has((b as any).bin_id) ? true : false,
		}));
	}

	private mapBinsByCoordinates(bins: API.Bin[]) {
		const groups: Record<string, API.Bin[]> = Object.create(null);
	  
		for (const b of bins) {
		  const loc = b?.Bin_location;
		  const lat = loc?.bin_latitude;
		  const lon = loc?.bin_longitude;
	  
		  if (lat == null || lon == null) continue;
	  
		  const key = `${lat},${lon}`;
		  (groups[key] ??= []).push(b);
		}
		return groups;
	}

	private mapBinsByClusterUsingSystem(bins) {
		const groups: Record<string, API.Bin[]> = Object.create(null);
	  
		for (const b of bins) {
		  const clusterId = (b as any)?.cluster_id;
		  const siteId = (b as any)?.site_id;	  
		  if (clusterId == null || siteId == null) continue;
		  const key = String(clusterId) + String(siteId);	  
		  (groups[key] ??= []).push(b);
		}
	  
		return groups;
	}

	private clusterToIcon(cluster){				
		let icon = '';		
		let redCluster = _.find(cluster, function (item) {
			return (item.Bin_live_Data.color_status_name == "RED")
		});	
	
		let yellowCluster = _.find(cluster, function (item) {
			return (item.Bin_live_Data.color_status_name == "YELLOW")
		});	

		let greenCluster = _.find(cluster, function (item) {
			return (item.Bin_live_Data.color_status_name == "GREEN")
		});	
		
		let nonActiveCluster = _.find(cluster, function (item) {
			return (item.bin_collection_mode == 1)
		});	
		if(nonActiveCluster != null){			
			if(cluster.length == 2){
				icon = `../../assets/images/bins/all/nonActiveCluster1.png`;
			}else if(cluster.length == 3){
				icon = `../../assets/images/bins/all/nonActiveCluster2.png`;
			}else if(cluster.length == 4){
				icon = `../../assets/images/bins/all/nonActiveCluster3.png`;
			}else{				
				icon = `../../assets/images/bins/all/nonActiveCluster4.png`;
			}			
			return icon;
		}else if(nonActiveCluster == null && redCluster != null){
			if(cluster.length == 2){
				icon = `../../assets/images/bins/all/redCluster1.png`;
			}else if(cluster.length == 3){
				icon = `../../assets/images/bins/all/redCluster2.png`;
			}else if(cluster.length == 4){
				icon = `../../assets/images/bins/all/redCluster3.png`;
			}else{
				icon = `../../assets/images/bins/all/redCluster4.png`;
			}			
			return icon;
		}else if(nonActiveCluster == null && redCluster == null && yellowCluster != null){
			if(cluster.length == 2){
				icon = `../../assets/images/bins/all/yellowCluster1.png`;
			}else if(cluster.length == 3){
				icon = `../../assets/images/bins/all/yellowCluster2.png`;
			}else if(cluster.length == 4){
				icon = `../../assets/images/bins/all/yellowCluster3.png`;
			}else{
				icon = `../../assets/images/bins/all/yellowCluster4.png`;
			}			
			return icon;
		}else if(nonActiveCluster == null && redCluster == null && yellowCluster == null && greenCluster != null){
			if(cluster.length == 2){
				icon = `../../assets/images/bins/all/greenCluster1.png`;
			}else if(cluster.length == 3){
				icon = `../../assets/images/bins/all/greenCluster2.png`;
			}else if(cluster.length == 4){
				icon = `../../assets/images/bins/all/greenCluster3.png`;
			}else{
				icon = `../../assets/images/bins/all/greenCluster4.png`;
			}			
			return icon;
		}
	}

	private BinToIcon(status,mode) {
		let icon = '';
		if(mode == "1"){
			icon = `../../assets/images/bins/all/nonActive.svg`;
			return icon;
		}else{
			if(this.usageType == 1){
				if(status == 0){
					icon = `../../assets/images/bins/all/greenNotOrdered.png`;
				}else if(status == 1){
					icon = `../../assets/images/bins/all/yellowNotOrdered.png`;
				}else if(status == 2){
					icon = `../../assets/images/bins/all/redNotOrdered.png`;
				}
				return icon;
			}else{
				if(status == 0){
					icon = `../../assets/images/bins/all/greenNotOrdered2.png`;
				}else if(status == 1){
					icon = `../../assets/images/bins/all/yellowNotOrdered2.png`;
				}else if(status == 2){
					icon = `../../assets/images/bins/all/redNotOrdered2.png`;
				}
				return icon;
			}
		}
	}
}
